/**
 * Adapter layer that bridges the forecast host app's hooks/providers
 * into the gecko-asset-viz package's provider system.
 *
 * This file wires the host app's theme, data-fetching, and locale hooks
 * into the provider interfaces expected by gecko-asset-viz.
 */
import { AssetViz } from '@asset-viz/AssetViz'
import { AssetVizController, type AssetVizProps } from '@asset-viz/defines'
import { AssetVizDataProvider } from '@asset-viz/providers/data'
import { AssetVizLocaleProvider } from '@asset-viz/providers/locale'
import { AssetVizThemeProvider } from '@asset-viz/providers/theme'
import {
  type AssetVizDataConfig,
  type AssetVizLocaleConfig,
  type AssetVizTheme,
} from '@asset-viz/types/providers'
import { WebGLContext } from 'features/assetDatabase/contexts/WebGLLayer'
import {
  useCdfBinReading,
  useCdfRawValues,
  useColorScale,
} from 'features/dataLayers'
import { useAxisNames, useGeckoLocale } from 'features/locale'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useContext, useMemo } from 'react'
import { useGeckoTheme } from 'styles/GeckoTheme'

// ---------------------------------------------------------------------------
// Theme Adapter
// ---------------------------------------------------------------------------

function useAssetVizThemeFromGecko(): AssetVizTheme {
  const { theme } = useGeckoTheme()
  return useMemo(
    () => ({
      activeText: theme.forecast.activeText,
      background1: theme.forecast.background1,
      background2: theme.forecast.background2,
      background2_5: theme.forecast.background2_5,
      background3: theme.forecast.background3,
      defaultText: theme.forecast.defaultText,
      secondaryText: theme.forecast.secondaryText,
      border: theme.forecast.border,
      icon1: theme.forecast.icon1,
    }),
    [theme]
  )
}

// ---------------------------------------------------------------------------
// Data Adapter
// ---------------------------------------------------------------------------

function useAssetVizDataFromGecko(): Partial<AssetVizDataConfig> {
  return useMemo(
    () => ({
      useColorScale,
      useCdfRawValues,
      useCdfBinReading,
      useResolveAssetUrl: (params: { entryId: string; path: string }) => {
        const { url, isLoading } = useRolodexSignedURLs({
          entry_id: params.entryId,
          path: params.path,
        })
        return { url, isLoading }
      },
      useWebGLContext: () => useContext(WebGLContext),
    }),
    []
  )
}

// ---------------------------------------------------------------------------
// Locale Adapter
// ---------------------------------------------------------------------------

function useAssetVizLocaleFromGecko(): Partial<AssetVizLocaleConfig> {
  const {
    formatMeasurement,
    formatMeasurementRange,
    getPreferredMeasurementUnit,
    formatRange,
  } = useGeckoLocale()

  return useMemo(
    () => ({
      formatMeasurement,
      formatMeasurementRange,
      getPreferredMeasurementUnit,
      formatRange,
      getAxisNames: useAxisNames,
    }),
    [
      formatMeasurement,
      formatMeasurementRange,
      getPreferredMeasurementUnit,
      formatRange,
    ]
  )
}

// ---------------------------------------------------------------------------
// Combined Provider
// ---------------------------------------------------------------------------

/**
 * Wraps children with all three gecko-asset-viz providers, wired to the
 * forecast host app's hooks and context.
 *
 * Place this around any component tree that renders AssetViz.
 */
export const ForecastAssetViz = React.forwardRef<
  AssetVizController,
  AssetVizProps & {
    className?: string | undefined
    children?: React.ReactNode
  }
>(({ children, ...props }, ref) => {
  const theme = useAssetVizThemeFromGecko()
  const dataConfig = useAssetVizDataFromGecko()
  const localeConfig = useAssetVizLocaleFromGecko()

  return (
    <AssetVizThemeProvider theme={theme}>
      <AssetVizDataProvider config={dataConfig}>
        <AssetVizLocaleProvider config={localeConfig}>
          <AssetViz ref={ref} {...props}>
            {children}
          </AssetViz>
        </AssetVizLocaleProvider>
      </AssetVizDataProvider>
    </AssetVizThemeProvider>
  )
})

ForecastAssetViz.displayName = 'ForecastAssetViz'
